/**
 * SystemBuilderDrawer - Handles product info drawer functionality
 * @class
 * @extends {window.BAO.CustomElement}
 */
class SystemBuilderDrawer extends window.BAO.CustomElement() {
  /**
   * Context for the currently open info drawer
   * @type {Object|null}
   * @property {string} groupIndex - Product group index
   * @property {string} productUrl - Product URL for fetching info
   * @property {number} subSystemIndex - Subsystem index
   * @property {number} layerIndex - Layer index
   */
  infoDrawerContext = null

  /**
   * Called when element is connected to DOM
   */
  connectedCallback () {
    window.BAO.SystemBuilder.attach(this)
    super.connectedCallback()
  }

  /**
   * Setup event listeners for drawer interactions
   */
  setupListeners () {
    super.setupListeners()

    this.handleProductInfoTrigger = this.handleProductInfoTrigger.bind(this)
    this.handleInfoDrawer = this.handleInfoDrawer.bind(this)

    this.sharedEls?.['product-info-trigger']?.elements.forEach((trigger) => {
      this.listeners.add(trigger, 'click', this.handleProductInfoTrigger)
    })

    const footerContainer = this.sharedEls?.['product-info-footer']?.element
    if (footerContainer) {
      this.listeners.add(footerContainer, 'click', this.handleInfoDrawer)
      this.listeners.add(footerContainer, 'change', this.handleInfoDrawer)
    }
  }

  /**
   * Handle product info trigger click - fetches and displays product info in drawer
   * @param {Event} event - Click event
   */
  async handleProductInfoTrigger (event) {
    event.preventDefault()
    event.stopPropagation()

    const trigger = event.target.closest(
      this.sharedEls?.['product-info-trigger']?.selector,
    )
    if (!trigger) return

    const productUrl = trigger.dataset.productUrl
    const groupIndex = trigger.dataset.systemGroupIndex
    const subSystemIndex = parseInt(trigger.dataset.systemSubsystemIndex)
    const layerIndex = parseInt(trigger.dataset.systemLayerIndex)
    if (!productUrl) return

    this.infoDrawerContext = {
      groupIndex,
      productUrl,
      subSystemIndex,
      layerIndex,
    }

    const contentContainer = this.sharedEls['product-info-content']?.element
    const footerContainer = this.sharedEls['product-info-footer']?.element
    if (!contentContainer) return

    const html = await window.BAO.sectionRenderer.fetchSingle(
      'system-builder-product-info',
      productUrl,
    )

    const doc = new DOMParser().parseFromString(html, 'text/html')
    const content = doc.querySelector(
      '[data-system-builder-external-el="product-info"]',
    )
    const footer = doc.querySelector(
      '[data-system-builder-external-el="product-info-footer"]',
    )

    if (content) {
      contentContainer.innerHTML = content.innerHTML
    }

    if (footer && footerContainer) {
      footerContainer.innerHTML = footer.innerHTML

      const productCard = trigger.closest(this.sharedEls.product.selector)
      const productSelect = productCard?.querySelector(
        this.sharedEls.select.selector,
      )
      const drawerSelect = footerContainer.querySelector(
        this.sharedEls.select.selector,
      )
      const drawerButton = footerContainer.querySelector(
        '[data-system-builder-el="drawer-button"]',
      )

      if (productSelect?.value && drawerSelect) {
        drawerSelect.value = productSelect.value
        drawerButton.disabled = false
      }
    }

    window.drawers.open('system-builder-product-info')
  }

  /**
   * Handle drawer interactions - variant selection and "Add to System" button
   * @param {Event} event - Change or click event
   */
  handleInfoDrawer (event) {
    const footer = event.currentTarget
    const drawerSelect = footer.querySelector(this.sharedEls.select.selector)
    const drawerButton = footer.querySelector(
      '[data-system-builder-el="drawer-button"]',
    )

    if (event.target.closest(this.sharedEls.select.selector)) {
      drawerButton.disabled = !drawerSelect?.value
      return
    }

    if (event.target.closest('[data-system-builder-el="drawer-button"]')) {
      if (!drawerSelect?.value) return

      const { groupIndex, productUrl, subSystemIndex, layerIndex } =
        this.infoDrawerContext
      if (groupIndex === undefined || !productUrl) return

      const allProducts = this.getAllProductsForLayer(
        subSystemIndex,
        layerIndex,
      )
      const matchingProduct = allProducts.find((product) => {
        if (product.dataset.systemGroupIndex !== String(groupIndex)) {
          return false
        }

        const infoTrigger = product.querySelector(
          this.sharedEls['product-info-trigger'].selector,
        )

        return infoTrigger?.dataset.productUrl === productUrl
      })

      if (!matchingProduct) return

      const productSelect = matchingProduct.querySelector(
        this.sharedEls.select.selector,
      )
      if (!productSelect) return

      productSelect.value = drawerSelect.value
      productSelect.dispatchEvent(new Event('change', { bubbles: true }))

      window.drawers.close('system-builder-product-info')
    }
  }

  /**
   * Get all product elements for a specific layer
   * @param {number} subSystemIndex - Subsystem index
   * @param {number} layerIndex - Layer index
   * @returns {Array<HTMLElement>} Array of product elements
   */
  getAllProductsForLayer (subSystemIndex, layerIndex) {
    return window.BAO.SystemBuilder.getAllProductsForLayer(
      subSystemIndex,
      layerIndex,
    )
  }
}

window.customElements.define('system-builder-drawer', SystemBuilderDrawer)
